// frontend/src/App.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { Socket } from 'socket.io-client';
import './styles.css';
import { ExtendedMessage, availableModels, ModelId, ConversationPreview, Conversation } from './types';
import { initializeSocket } from './socket';
import Auth from './components/Auth';
import ChatInterface from './components/ChatInterface';
import ConversationSidebar from './components/ConversationSidebar';
import { getConversations, getConversation, checkAuth } from './api';
import { saveLastConversationId } from './utils/localStores';
import { BACKEND_URL } from './constants';
import apiClient from './api';

const App: React.FC = () => {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const [authError, setAuthError] = useState<string | null>(null);
    const [inputValue, setInputValue] = useState<string>('');
    const [messages, setMessages] = useState<ExtendedMessage[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectedModel, setSelectedModel] = useState<ModelId>(availableModels[0].id);
    const [isSocketConnected, setIsSocketConnected] = useState<boolean>(false);
    const [isLmStudioConnected, setIsLmStudioConnected] = useState<boolean>(false);
    const [socketInstance, setSocketInstance] = useState<{ socket: Socket | null; cleanup?: () => void } | null>(null);
    const [conversations, setConversations] = useState<ConversationPreview[]>([]);
    const [currentConversation, setCurrentConversation] = useState<Conversation | null>(null);
    const [loadingConversations, setLoadingConversations] = useState<boolean>(false);
    const [modelLoading, setModelLoading] = useState<{ [key: string]: boolean }>({});
    const [modelChangeCount, setModelChangeCount] = useState<number>(0);
    const [lastModelChange, setLastModelChange] = useState<number>(0);
    const [showModelChangeConfirm, setShowModelChangeConfirm] = useState<{ modelId: ModelId | null }>({ modelId: null });
    const [isModalLocked, setIsModalLocked] = useState<boolean>(false);
    const [theme, setTheme] = useState<string>(() => {
        const savedTheme = localStorage.getItem('theme');
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        console.log('Initialisation du thème - savedTheme:', savedTheme, 'prefersDark:', prefersDark);
        return savedTheme || (prefersDark ? 'dark' : 'light');
    });

    useEffect(() => {
        console.log('Application de data-theme:', theme);
        document.documentElement.setAttribute('data-theme', theme);
        localStorage.setItem('theme', theme);
    }, [theme]);

    const toggleTheme = () => {
        setTheme((prevTheme) => {
            const newTheme = prevTheme === 'light' ? 'dark' :
                            prevTheme === 'dark' ? 'theme-neon' :
                            prevTheme === 'theme-neon' ? 'theme-pastel' : 'light';
            console.log('Changement de thème - Ancien:', prevTheme, 'Nouveau:', newTheme);
            return newTheme;
        });
    };

    const handleCheckAuth = async (): Promise<void> => {
        try {
            const user = await checkAuth();
            if (user) {
                setIsAuthenticated(true);
                setAuthError(null);
            } else {
                setIsAuthenticated(false);
                setAuthError('Utilisateur non authentifié. Veuillez vous reconnecter.');
            }
        } catch (err) {
            console.error('Authentication check failed:', err);
            setIsAuthenticated(false);
            setAuthError('Erreur lors de la vérification de l’authentification. Veuillez vous reconnecter.');
        }
    };

    const checkLmStudioStatus = useCallback(async () => {
        try {
            const response = await apiClient.get('/api/check-lmstudio');
            setIsLmStudioConnected(response.data.status === 'online');
        } catch (error) {
            console.error('Erreur lors de la vérification de LM Studio:', error);
            setIsLmStudioConnected(false);
        }
    }, []);

    const loadConversations = useCallback(async (): Promise<void> => {
        if (!isAuthenticated) return;
        setLoadingConversations(true);
        try {
            const response = await getConversations();
            if (!response || !Array.isArray(response.conversations)) {
                throw new Error('Réponse des conversations invalide');
            }
            setConversations(response.conversations);
        } catch (error) {
            console.error('Error loading conversations:', error);
            setConversations([]);
        } finally {
            setLoadingConversations(false);
        }
    }, [isAuthenticated]);

    const handleLogout = useCallback(async (): Promise<void> => {
        try {
            await fetch(`${BACKEND_URL}/api/logout`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'X-CSRF-Token': localStorage.getItem('csrfToken') || '',
                },
            });
            localStorage.removeItem('token');
            localStorage.removeItem('csrfToken');
            setIsAuthenticated(false);
            setMessages([]);
            setCurrentConversation(null);
            setConversations([]);
            if (socketInstance?.cleanup) socketInstance.cleanup();
            setSocketInstance(null);
            console.log('Déconnexion réussie');
        } catch (err) {
            console.error('Error logging out:', err);
            localStorage.removeItem('token');
            localStorage.removeItem('csrfToken');
            setIsAuthenticated(false);
            setMessages([]);
            setCurrentConversation(null);
            setConversations([]);
            if (socketInstance?.cleanup) socketInstance.cleanup();
            setSocketInstance(null);
        }
    }, [socketInstance]);

    const handleLogin = async (): Promise<void> => {
        try {
            await handleCheckAuth();
            if (isAuthenticated) {
                if (socketInstance?.cleanup) socketInstance.cleanup();
                const newSocket = initializeSocket(isAuthenticated, setIsSocketConnected, setMessages, setIsLoading);
                setSocketInstance(newSocket);
            }
        } catch (error) {
            console.error('Erreur lors du login:', error);
            setAuthError('Erreur lors de la connexion. Veuillez réessayer.');
        }
    };

    const createNewConversation = (): void => {
        setCurrentConversation(null);
        setMessages([]);
        saveLastConversationId(undefined);
    };

    const selectConversation = async (id: string): Promise<void> => {
        if (typeof id !== 'string' || id.trim() === '') {
            console.error('ID de conversation invalide');
            return;
        }
        try {
            const conversation = await getConversation(id);
            if (!conversation || typeof conversation._id !== 'string') {
                throw new Error('Conversation invalide');
            }
            setCurrentConversation(conversation);
            const formattedMessages: ExtendedMessage[] = conversation.messages.map((msg) => {
                if (typeof msg.content !== 'string' || !['user', 'assistant', 'system', 'ia'].includes(msg.role)) {
                    throw new Error('Message de conversation invalide');
                }
                return {
                    sender: msg.role === 'user' ? 'user' : 'ia',
                    content: msg.content,
                    type: 'text',
                    modelName: conversation.modelName,
                    timestamp: typeof msg.timestamp === 'string' ? msg.timestamp : new Date(msg.timestamp).toISOString(),
                    final: true,
                    conversationId: conversation._id,
                };
            });
            setMessages(formattedMessages);
            saveLastConversationId(conversation._id);
            setSelectedModel(conversation.modelName);
        } catch (error) {
            console.error('Error fetching conversation:', error);
        }
    };

    const preloadModel = useCallback(async (modelId: ModelId) => {
        if (!socketInstance?.socket || !isSocketConnected) return;

        if (typeof modelId !== 'string' || !availableModels.some(model => model.id === modelId)) {
            console.error('Model ID invalide:', modelId);
            return;
        }

        setModelLoading((prev) => ({ ...prev, [modelId]: true }));
        try {
            socketInstance.socket.emit('preload-model', { model: modelId });
            await new Promise((resolve) => setTimeout(resolve, 1000));
        } catch (error) {
            console.error(`Erreur lors du préchargement du modèle ${modelId}:`, error);
        } finally {
            setModelLoading((prev) => ({ ...prev, [modelId]: false }));
        }
    }, [socketInstance, isSocketConnected]);

    const handleModelChange = useCallback((modelId: ModelId) => {
        if (typeof modelId !== 'string' || !availableModels.some(model => model.id === modelId)) {
            console.error('Model ID invalide:', modelId);
            return;
        }

        const now = Date.now();
        setModelChangeCount((prev) => prev + 1);
        setLastModelChange(now);

        if (modelChangeCount >= 5 && now - lastModelChange < 30000) {
            console.warn('Trop de changements de modèle, préchargement désactivé temporairement');
            setSelectedModel(modelId);
            localStorage.setItem('preferredModel', modelId);
            return;
        }

        const isGenerating = messages.some(msg => msg.sender === 'ia' && !msg.final);
        if (currentConversation && !isModalLocked && !isGenerating) {
            console.log('Affichage de la modale pour le modèle:', modelId);
            setShowModelChangeConfirm({ modelId });
            setIsModalLocked(true);
        } else if (!currentConversation) {
            setSelectedModel(modelId);
            localStorage.setItem('preferredModel', modelId);
            preloadModel(modelId);
        }
    }, [modelChangeCount, lastModelChange, preloadModel, currentConversation, isModalLocked, messages]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setModelChangeCount(0);
        }, 30000);
        return () => clearTimeout(timer);
    }, [modelChangeCount]);

    const onSendMessage = useCallback(
        async (message: string): Promise<void> => {
            if (typeof message !== 'string' || !message.trim()) {
                console.error('Message invalide');
                return;
            }
            if (!socketInstance?.socket || !isSocketConnected || isLoading) {
                console.warn('Socket non connecté ou en chargement');
                return;
            }

            setIsLoading(true);

            const userMessage: ExtendedMessage = {
                sender: 'user',
                content: message,
                type: 'text',
                timestamp: new Date().toISOString(),
                final: true,
                conversationId: currentConversation?._id,
            };

            setMessages((prev) => {
                console.log('Mise à jour des messages:', prev.length, '->', prev.length + 1);
                return [...prev, userMessage];
            });

            const messagePayload = {
                content: message,
                model: selectedModel,
                conversationId: currentConversation?._id,
            };

            try {
                console.log('Envoi du message avec conversationId:', messagePayload.conversationId || 'nouvelle conversation');
                socketInstance.socket.emit('message', messagePayload);

                if (!currentConversation) {
                    socketInstance.socket.once('messageStatus', (status: { status: string; conversationId: string }) => {
                        if (!status || typeof status.conversationId !== 'string') {
                            throw new Error('Statut de message invalide');
                        }
                        console.log('Nouvelle conversation créée par le backend, ID:', status.conversationId);
                        getConversation(status.conversationId).then((conversation) => {
                            if (conversation) {
                                setCurrentConversation(conversation);
                                saveLastConversationId(conversation._id);
                                setMessages((prev) =>
                                    prev.map((msg) => (!msg.conversationId ? { ...msg, conversationId: conversation._id } : msg))
                                );
                            }
                        });
                    });
                }

                setTimeout(() => loadConversations(), 1000);
            } catch (error) {
                console.error('Erreur lors de l\'envoi du message:', error);
                setIsLoading(false);
            }
        },
        [socketInstance, isSocketConnected, isLoading, selectedModel, currentConversation, loadConversations]
    );

    useEffect(() => {
        handleCheckAuth();
    }, []);

    useEffect(() => {
        if (isAuthenticated && !socketInstance) {
            const newSocket = initializeSocket(isAuthenticated, setIsSocketConnected, setMessages, setIsLoading);
            setSocketInstance(newSocket);
        }
        return () => {
            if (socketInstance?.cleanup) socketInstance.cleanup();
        };
    }, [isAuthenticated, socketInstance]);

    useEffect(() => {
        if (isAuthenticated) {
            loadConversations();
            checkLmStudioStatus();
        }
    }, [isAuthenticated, loadConversations, checkLmStudioStatus]);

    useEffect(() => {
        console.log('État isSocketConnected changé:', isSocketConnected);
    }, [isSocketConnected]);

    useEffect(() => {
        console.log('État currentConversation changé:', currentConversation?._id);
    }, [currentConversation]);

    return (
        <div className={`App ${theme}`}>
            <div className="app__header">
                <h1 className="app__title">Typia</h1>
                {isAuthenticated && (
                    <div className="header-controls">
                        <div className="app__status">
                            <div className={`status ${isSocketConnected ? 'online' : 'offline'}`}>
                                {isSocketConnected ? 'Serveur en ligne' : 'Serveur hors ligne'}
                            </div>
                            <div className="lmstudio-status">
                                <span className={`status-dot ${isLmStudioConnected ? 'online' : 'offline'}`}></span>
                                <span>{isLmStudioConnected ? 'LM Studio en ligne' : 'LM Studio hors ligne'}</span>
                            </div>
                        </div>
                        <button
                            onClick={toggleTheme}
                            className="theme-toggle"
                            aria-label="Changer de thème"
                            title="Changer de thème" // Ajout d'un tooltip pour clarifier l'action
                        >
                            {theme === 'light' ? '🌙' : theme === 'dark' ? '☀️' : theme === 'theme-neon' ? '🎨' : '🌸'}
                        </button>
                        <button onClick={handleLogout} className="logout-button" aria-label="Déconnexion">
                            Déconnexion
                        </button>
                    </div>
                )}
            </div>
            {!isAuthenticated ? (
                <>
                    {authError && <div className="error">{authError}</div>}
                    <Auth onLogin={handleLogin} />
                </>
            ) : (
                <>
                    {authError && <div className="error">{authError}</div>}
                    <div className="controls">
                        <div className="button-group model-buttons">
                            {availableModels.map((model) => (
                                <div key={model.id} className="model-button-wrapper">
                                    <button
                                        onClick={() => handleModelChange(model.id)}
                                        className={`button button--secondary ${selectedModel === model.id ? 'active' : ''}`}
                                        title={`Modèle: ${model.name}`}
                                        aria-label={`Changer pour le modèle ${model.name}`}
                                    >
                                        {model.name}
                                        {modelLoading[model.id] && (
                                            <span className="loading-dot">⏳</span>
                                        )}
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                    {showModelChangeConfirm.modelId && (
                        <div className="model-change-overlay">
                            <div className="model-change-confirm">
                                <p>
                                    Voulez-vous créer une nouvelle conversation avec le modèle{' '}
                                    {availableModels.find((m) => m.id === showModelChangeConfirm.modelId)?.name} ?
                                </p>
                                <button
                                    onClick={() => {
                                        console.log('Création d\'une nouvelle conversation avec le modèle:', showModelChangeConfirm.modelId);
                                        createNewConversation();
                                        setSelectedModel(showModelChangeConfirm.modelId!);
                                        localStorage.setItem('preferredModel', showModelChangeConfirm.modelId!);
                                        preloadModel(showModelChangeConfirm.modelId!);
                                        setShowModelChangeConfirm({ modelId: null });
                                        setIsModalLocked(false);
                                    }}
                                    aria-label="Créer une nouvelle conversation"
                                >
                                    Oui, nouvelle conversation
                                </button>
                                <button
                                    onClick={() => {
                                        console.log('Continuation dans la conversation actuelle avec le modèle:', showModelChangeConfirm.modelId);
                                        setSelectedModel(showModelChangeConfirm.modelId!);
                                        localStorage.setItem('preferredModel', showModelChangeConfirm.modelId!);
                                        preloadModel(showModelChangeConfirm.modelId!);
                                        setShowModelChangeConfirm({ modelId: null });
                                        setIsModalLocked(false);
                                    }}
                                    aria-label="Continuer dans cette conversation"
                                >
                                    Non, continuer dans cette conversation
                                </button>
                                <button
                                    onClick={() => {
                                        console.log('Annulation du changement de modèle');
                                        setShowModelChangeConfirm({ modelId: null });
                                        setIsModalLocked(false);
                                    }}
                                    aria-label="Annuler"
                                >
                                    Annuler
                                </button>
                            </div>
                        </div>
                    )}
                    <div className="chat-with-sidebar">
                        <ConversationSidebar
                            conversations={conversations}
                            loading={loadingConversations}
                            onSelect={selectConversation}
                            onNew={createNewConversation}
                            currentId={currentConversation?._id}
                            onConversationUpdated={loadConversations}
                        />
                        <div className="chat-main">
                            <ChatInterface
                                inputValue={inputValue}
                                setInputValue={setInputValue}
                                messages={messages}
                                onSendMessage={onSendMessage}
                                isLoading={isLoading}
                                theme={theme as 'light' | 'dark' | 'neon' | 'pastel'}
                                modelId={selectedModel}
                                socket={socketInstance?.socket}
                            />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default App;